!<template>
    <div class="call--area">
        <panel-call-details
            v-bind="{
                headerClass: 'call__header',
                data: {
                    contactRequest: {
                        internalPersonProfile: settings.data.internalPersonProfile,
                        request: settings.data.request,
                        observations: settings.data.observations,
                        internalObservations: settings.data.internalObservations,
                        receptionDate: settings.data.receptionDate,
                        warnings: settings.data.contactWarnings,
                        callHistory: settings.data.contactCallHistory
                    },
                    script: settings.data.script
                },
                close: { show: false }
            }"
        >
            <template #panel__header>
                <o-header
                    :showTimer="settings.showTimer"
                    :timeBetweenCalls="settings.timeBetweenCalls"
                    :callIsActive="!call.end"
                    :isOnHold="call.isOnHold"
                    :disableRecording="call.disableRecording"
                    :callHasBeenConfirmed="call.confirmed"
                    :callNumber="call.number"
                    :workflow="workflow"
                    :blockTypification="settings.blockTypification"
                    @record-call="clickToggleRecord"
                    @hold-call="clickToggleHold"
                    @stop-call="hangUpAndActiveteTimer"
                    @type-call="show.typification = true"
                    @trigger-new-request="resetAndExecuteNewRequest"
                    @trigger-pause="workflow = 'pause'"
                    @trigger-leave="workflow = 'leave'"
                />
            </template>
        </panel-call-details>
        <modal
            v-if="show.typification"
            :form="{
                class: 'call__modal',
                settings: {
                    id: 'formModalCall',
                    typeOfPreparation: 'call'
                }
            }"
            :customSettings="{
                color: 'green',
                class: 'call__modal__content',
                button: {
                    label: 'confirmEvaluation'
                },
                show: {
                    infoSection: false
                }
            }"
            :preventReset="true"
            @close="show.typification = false"
            :key="call.end" >
            <template #modal__header >
                <h2 class="call__modal__header">{{ $t('global.callEvaluation') }}</h2>
            </template>
            <template #modal__body >
                <div class="cal__modal__body">
                    <div class="call__modal__icons display--flex">
                        <button-with-icon
                            :icon="{
                                component: () =>
                                    import( /* webpackChunkName: 'IconPositive' */ '@/components/bundles/layout/images/icons/IconPositive' ),
                                    class: `
                                        call__modal__icon
                                        call__modal__icon--green
                                        pointer
                                        ${ call.type === 'successful' ? 'call__modal__icon--green--active' : '' }
                                    `
                            }"
                            class="call__modal__icons__button"
                            :action="{
                                execute: true,
                                function: chooseTypeOfState
                            }"
                        />
                        <button-with-icon
                            :icon="{
                                component: () =>
                                    import( /* webpackChunkName: 'IconReschedule' */ '@/components/bundles/layout/images/icons/IconReschedule' ),
                                class: `
                                    call__modal__icon
                                    call__modal__icon--reschedule
                                    pointer
                                    ${ call.type === 'rescheduling' ? 'call__modal__icon--reschedule--active' : '' }
                                `
                            }"
                            class="call__modal__icons__button"
                            :action="{
                                execute: true,
                                function: chooseTypeOfState,
                                param: 'rescheduling'
                            }"
                        />
                        <button-with-icon
                            :icon="{
                                component: () =>
                                    import( /* webpackChunkName: 'IconNegative' */ '@/components/bundles/layout/images/icons/IconNegative' ),
                                class: `
                                    call__modal__icon
                                    call__modal__icon--red
                                    pointer
                                    ${ call.type === 'unsuccessful' ? 'call__modal__icon--red--active' : '' }
                                `
                            }"
                            class="call__modal__icons__button"
                            :action="{
                                execute: true,
                                function: chooseTypeOfState,
                                param: 'unsuccessful'
                            }"
                        />
                    </div>
                    <transition v-if="show.rescheduling" name="call__modal--transition" appear >
                        <div class="call__modal__rescheduling">
                            <base-field
                                class="call__modal__input"
                                :settings="{
                                    label: { class: 'field__label--hide', text: $t('global.rescheduleTo') },
                                    input: {
                                        component: 'InputCalendar',
                                        name: 'rescheduleTo',
                                        id: 'rescheduleTo',
                                        targetClass: 'field__input--default ty+pography--default color--default',
                                        value: getDateISO,
                                        browserAutocomplete: 'off',
                                        dynamicData: {
                                            configs: {
                                                mode: 'dateTime',
                                                is24hr: true,
                                                minDate: getDate,
                                                format: dateFormat,
                                                hideInput: true
                                            },
                                            isInline: true
                                        },
                                        isMissing: errors.missing.rescheduleTo,
                                        fileValidation: 'isAfterCertainDate',
                                        errors: [{
                                            type: 'isAfter', warning: $t('error.isAfter', { date: $t('global.beginDate') })
                                        }],
                                        errorOnSubmit: {
                                            show: errors.api.rescheduleTo ? true : false,
                                            messages: errors.api.rescheduleTo
                                        },
                                        validationParameter: getDate,
                                        noListener: true
                                    },
                                    allowFilling: true
                                }"
                            />
                            <base-field
                                class="call__modal__input call__modal__input--new-contact"
                                :settings="{
                                    label: { class: 'capitalize', text: $t('global.newContact') },
                                    input: {
                                        component: 'InputBasic',
                                        id: 'newContactValue',
                                        name: 'newContactValue',
                                        type: 'text',
                                        class: 'field__input--default typography--default color--default',
                                        value: '',
                                        browserAutocomplete: 'newContact',
                                        errorOnSubmit: {
                                            show: errors.api.newContactValue ? true : false,
                                            messages: errors.api.newContactValue
                                        },
                                        noListener: true
                                    },
                                    allowFilling: true
                                }"
                            />
                        </div>
                    </transition>
                    <base-field
                        class="call__modal__input"
                        :settings="{
                            label: { class: 'call__modal__label capitalize', text: $t('global.selectACause') },
                            input: {
                                component: 'InputAutocomplete',
                                id: 'stateId',
                                name: 'stateId',
                                type: 'text',
                                value: drop.value,
                                targetClass: 'field__input--default typography--default color--default',
                                browserAutocomplete: 'off',
                                isRequired: true,
                                isMissing: errors.missing.stateId,
                                dynamicData: {
                                    data: call.states.drop
                                },
                                noListener: true
                            },
                            allowFilling: drop.active
                        }"
                    />
                    <!-- eslint-disable max-len -->
                    <button
                        v-if="sendEmail.allowed"
                        class="call__modal__button--send-email background--transparent accessibility--button pointer color--green display--flex"
                        :class="{  'disable': loading }"
                        @click.prevent="show.sendEmail = true; show.typification = false;"
                        :disabled="loading"
                    >
                        <span class="display--block capitalize">{{ $t('global.sendEmail') }}</span>
                        <icon-send-email class="call__modal__button--send-email__icon"/>
                    </button>
                </div>
            </template>
        </modal>
        <modal
            v-if="show.sendEmail"
            :form="{
                class: 'send-email__modal',
                settings: {
                    id: 'formModalSendEmail',
                    typeOfPreparation: 'sendEmail'
                }
            }"
            :customSettings="{
                color: 'green',
                class: 'send-email__modal__content',
                button: {
                    label: 'sendEmail'
                },
                show: {
                    infoSection: false
                }
            }"
            :preventReset="true"
            @close="show.sendEmail = false"
            :key="`modalSendEmail`">
            <template #modal__header >
                <h2 class="send-email__modal__header">{{ $t('global.sendEmail') }}</h2>
            </template>
            <template #modal__body >
                <div class="send-email__modal__body">
                    <base-field
                        class="send-email__modal__input send-email__modal__input--template"
                        :settings="{
                            label: { class: 'send-email__modal__label capitalize', text: $t('global.mailTemplates') },
                            input: {
                                component: 'InputAutocomplete',
                                id: 'templateId',
                                name: 'templateId',
                                type: 'text',
                                value: '',
                                targetClass: 'field__input--default typography--default color--default',
                                browserAutocomplete: 'off',
                                isRequired: true,
                                isMissing: errors.missing.templateId,
                                dynamicData: {
                                    data: sendEmail.templates.drop
                                },
                                noListener: true
                            },
                            allowFilling: true
                        }"
                    />

                    <base-field
                        class="send-email__modal__input send-email__modal__input--email"
                        :settings="{
                            label: { class: 'capitalize', text: $t('global.email') },
                            input: {
                                component: 'InputBasic',
                                id: 'email',
                                name: 'email',
                                type: 'text',
                                class: 'field__input--default typography--default color--default',
                                value: '',
                                browserAutocomplete: 'email',
                                isRequired: true,
                                isMissing: errors.missing.email,
                                fileValidation: 'basicEmail',
                                errors: [{
                                    type: 'email', warning: $t('error.email')
                                }],
                                errorOnSubmit: {
                                    show: errors.api.email ? true : false,
                                    messages: errors.api.email
                                }
                            },
                            allowFilling: true
                        }"
                    />
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
// packages
import {
    reactive,
    toRefs,
    computed,
    watch
} from '@vue/composition-api';
import { Locale } from 'v-calendar';

// jssip
import { call, toggleHold, hangUp } from '@/plugins/jssip';

// store
import { storePayload, mapState, useActions } from '@/hooks/use-store';

// models
import { toggleRequiredFields } from '@/models/helpers/toggle-required-fields';

// helpers
import { sortObjectsArray } from '@/helpers/mixinGeneral';

export default {
    components: {
        OHeader: () => import( /* webpackChunkName: 'OperateHeader' */ '@/components/bundles/operate/blocks/OHeader' ),
        PanelCallDetails: () => import( /* webpackChunkName: 'PanelCallDetails' */ '@/components/bundles/callDetails/childrens/PanelCallDetails' ),
        Modal: () => import( /* webpackChunkName: 'Modal' */ '@/components/bundles/layout/modals/Modal' ),
        ButtonWithIcon: () => import( /* webpackChunkName: 'ButtonWithIcon' */ '@/components/bundles/buttons/BaseButtonWithIcon' ),
        BaseField: () => import( /* webpackChunkName: 'BaseField'  */ '@/components/bundles/fields/BaseField' ),
        IconSendEmail: () => import( /* webpackChunkName: 'IconSendEmail' */ '@/components/bundles/layout/images/icons/IconSendEmail' )
    },
    props: {
        settings: {
            type: Object,
            default: () => ({ })
        },
        actions: Object
    },
    setup ( props, { emit }) {
        // definção do estado inicial
        const initialState = () => ({
            show: {
                typification: false,
                rescheduling: false,
                sendEmail: false
            },
            call: {
                id: props.settings.call.id || 0,
                number: props.settings.call.number || 0,
                end: false,
                states: {
                    all: props.settings.data.contactCallStates,
                    drop: []
                },
                ddis: props.settings.data.campaign.ddis,
                type: '',
                confirmed: false,
                isOnHold: false,
                disableRecording: false
            },
            sendEmail: {
                templates: {
                    drop: props.settings.data.campaign?.mailingTemplates.map( template => ({ value: template.id, label: template.name }) )
                },
                allowed: [ 'survey', 'meeting' ].includes( props.settings.data.campaign?.type?.name?.toLowerCase() )
            },
            drop: {
                active: true,
                value: 0
            },
            dateFormat: 'YYYY-MM-DD HH:mm',
            workflow: props.settings.workflow || 'newCall'
        });

        // definição de vars reactivas.
        const state = reactive({ ...initialState() });

        // store
        const { actions } = useActions();

        /**
         * Prepara o payload com dados que são necessários para quando se
         * for tipificar uma chamada ou enviar um e-mail.
         *
         * @since 1.2.3
         * @category Method
         * @autor Jorge Monteiro
         *
        */
        const preparePayload = () => {
            actions( 'registerField', { name: 'requestId', value: props.settings.data.id });
            actions( 'registerField', { name: 'id', value: state.call.id });
            actions( 'registerField', { name: 'disableRecording', value: state.call.disableRecording });
            if ( props.settings.data.internalObservations )
                actions( 'registerField', { name: 'internalObservations', value: props.settings.data.internalObservations });
        };

        preparePayload();

        // executa a chamada
        call({ settings: state.call });

        /**
         * [method]
         * Procedimento do reset do state após a
         * submissão para API ou quando o tempo
         * de preenchimento termina.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const resetState = () => {
            const {
                data, drop, show, ...rest
            } = initialState();

            Object.assign( state, {
                ...state,
                call: rest.call,
                sendEmail: rest.sendEmail,
                data,
                drop,
                show: {
                    ...show
                }
            });
        };

        /**
         * [method]
         * Quando o tempo de preenchimento acaba
         * sem que o utilizador tenha submitido a tipificação
         * é procedido ao reset do state e de seguida um novo
         * pedido a API para obtenção de uma nova chamada.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const resetAndExecuteNewRequest = () => {
            resetState();
            emit( 'new-request', 'menu' );
        };

        /**
         * Click para dar o toggle na chamada
         * em pausa ou não.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const clickToggleHold = () => {
            toggleHold({ isOnHold: state.call.isOnHold });
            state.call.isOnHold = !state.call.isOnHold;
        };

        /**
         * Click para dar o toggle na gravação da chamada.
         *
         * @category Method
         *
        */
        const clickToggleRecord = () => {
            state.call.disableRecording = !state.call.disableRecording;
            actions( 'registerField', { name: 'disableRecording', value: state.call.disableRecording });
        };

        // obtenção da store errors & trigger
        const { errors } = storePayload();
        const trigger = mapState({ module: 'storePayload', key: 'trigger' });
        const loading = mapState({ module: 'storePayload', key: 'loading' });

        watch( () => trigger.value,
            newUpdate => {
                if ( !newUpdate )
                    return;

                // Se o modal tiver aberto após submissão dos dados,
                // fecha o modal e prepara o payload outra vez.
                if ( state.show.sendEmail ) {
                    state.show.sendEmail = false;
                    preparePayload();
                    return;
                }

                resetState();

                switch ( state.workflow ) {
                    case 'pause':
                        emit( 'show-pause' );
                        break;
                    case 'leave':
                        emit( 'close-menu', true );
                        break;
                    case 'intercept':
                        emit( 'show-calls' );
                        break;
                    default:
                        emit( 'new-request', 'menu' );
                }

                state.workflow = 'newCall';
                actions( 'toggleTrigger', false );
            });

        /**
         * Escolha do tipo de estado.
         * A escolhe poderá ser feita entre sucesso e insucesso.
         * A apartir do tipo de estado é gerado os valores da drop.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const chooseTypeOfState = ( type = 'successful' ) => {
            state.call.type = type;

            const statesByType = state.call.states.all.filter( ({ name }) => name === `Call ${type}` );

            state.call.states.drop = statesByType[0].contactCallStates.map( ({ id, description }) => ({ value: id, label: description }) );
            // ordenar drop
            sortObjectsArray( state.call.states.drop );
            const states = state.call.states.drop;

            // se só houver 1 estado, é definido automaticamente esse.
            switch ( states.length ) {
                case 1:
                    actions( 'registerField', { name: 'stateId', value: states[0].value });
                    state.drop.value = states[0].label;
                    state.drop.active = false;
                    break;
                default:
                    actions( 'deleteField', { name: 'stateId' });
                    state.drop.value = 0;
                    state.drop.active = true;
                    break;
            }

            // caso o tipo seja re-agendar
            // é necessário apresentar o calendário & a input da hora
            // e também adicionar o campo como obrigatório.
            state.show.rescheduling = ( type === 'rescheduling' );

            if ( state.show.rescheduling ) {
                const locale = new Locale();
                actions( 'registerField', { name: 'rescheduleTo', value: locale.format( getDate.value, state.dateFormat ) });
            }

            toggleRequiredFields( 'call', { field: 'rescheduleTo', isRequired: state.show.rescheduling });
        };

        /**
         * [computed]
         * Obtenção da data actual.
         *
         * @since 1.0.0
         * @category Computed
         * @autor Tiago Martinho
         *
        */
        const getDate = computed( () => {
            // trigger para actualizar
            // a data.
            if ( !state.show.rescheduling )
                return null;

            const time = 1000 * 60 * 5; // transform em ms
            const date = new Date();
            return new Date( date.getTime() + time - ( date.getTime() % time ) );
        });

        /**
         * [computed]
         * Transformação da data para o formato ISO.
         *
         * @since 1.0.0
         * @category Computed
         * @autor Tiago Martinho
         *
        */
        const getDateISO = computed( () => {
            const date = new Date( getDate.value );
            return new Date( date.getTime() - ( date.getTimezoneOffset() * 60000 ) ).toISOString();
        });

        /**
         * [method]
         * Quando clica no botão de desligar a chamada, esta é
         * desligada e é ativado o timer para contar o tempo
         * que o utilizador tem para preencher os campos do guião.
         *
         * @since 1.2.8
         * @category Method
         * @autor Jorge Monteiro
         *
        */
        const hangUpAndActiveteTimer = () => {
            hangUp();
            emit( 'stop-call' );
        };

        return {
            ...toRefs( state ),
            resetAndExecuteNewRequest,
            clickToggleHold,
            clickToggleRecord,
            hangUpAndActiveteTimer,
            chooseTypeOfState,
            errors,
            loading,
            getDate,
            getDateISO,
            sortObjectsArray
        };
    }
};
</script>

<style lang="scss">
     // =============================================================================
    // Animations
    // =============================================================================
    @include keyframes( thumbs-up ) {
        0% {
            @include transform( translate( 15px, 12px ) rotate( 0 ) );
        }
        40% {
            @include transform( translate( 10px, 10px ) rotate( -10deg ) );
        }
        100% {
            @include transform( translate( 15px, 12px ) rotate( 0 ) );
        }
    }

    @include keyframes( thumbs-down ) {
        0% {
            @include transform( translate( 41px, 42px ) rotate( -180deg ) );
        }
        40% {
            @include transform( translate( 41px, 45px ) rotate( -190deg ) );
        }
        100% {
            @include transform( translate( 41px, 42px ) rotate( -180deg ) );
        }
    }

    // =============================================================================
    // Style
    // =============================================================================
    .call {
        &__modal {
            &--transition {
                &-enter-active, &-leave-active {
                    height: 412px;
                    opacity: 1;
                    overflow: hidden;
                    @include transition( height .3s ease-in-out, opacity .2s ease-in-out );
                }
                &-enter, &-leave-to {
                    height: 0;
                    opacity: 0;
                    @include transition( height .3s ease-in-out .2s, opacity .3s ease-in-out );
                    will-change: height, opacity;
                }
            }
            &__header {
                @include typography-specs( 25px, 33px, 700 );
            }
            &__content {
                max-width: 480px;
            }
            &__body {
                margin-top: 20px;
            }
            &__input {
                &--new-contact {
                    margin: 25px 0;
                }
            }
            &__icons {
                justify-content: center;
                margin-bottom: 45px;
                &__button {
                    @include transition( transfrom .3s ease-in-out );
                    will-change: transform;
                    &:active {
                        @include transform( scale( .8 ) );
                    }
                }
            }
            &__icon {
                @include transition( fill .3s ease-in-out );
                will-change: fill;
                &--reschedule {
                    margin: 0 20px;
                    &--active {
                        .circle {
                            fill: $color-green;
                        }
                        .path {
                            fill: $color-white;
                        }
                    }
                    .circle, .path {
                        @include transition( fill .3s ease-in-out );
                        will-change: fill;
                    }
                }
                &--green {
                    fill: rgba( $color-green, .6 );
                    &:hover {
                        fill: $color-green;
                        & .hand {
                            @include animation( thumbs-up .6s cubic-bezier( .73, .04, 1, .3 ) );
                        }
                    }
                    &--active {
                        fill: $color-green;
                    }
                }
                &--red {
                    fill: rgba( $color-red, .6 );
                    &:hover {
                        fill: $color-red;
                        & .hand {
                            @include animation( thumbs-down .6s cubic-bezier( .73, .04, 1, .3 ) );
                        }
                    }
                    &--active {
                        fill: $color-red;
                    }
                }
            }
            &__button {
                &--send-email {
                    width: 100%;
                    margin-top: 30px;
                    @include position-grid( center, center );
                    gap: 12px;
                }
            }
        }
    }
    .send-email {
        &__modal {
            &__content {
                max-width: 480px;
            }
            &__input {
                &--template {
                    margin-bottom: 25px
                }
            }
        }
    }
</style>